/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import GenericButton from '@ui-components/Button/GenericButton'
import GenericCard from '@ui-components/Card/GenericCard'
import { GenericTable, GenericTableColumn } from '@ui-components/Table/GenericTable'
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { getFilteredData, PortItem, portsDetailsColumns } from '../PortDetailsData';
import { fetchPortConnectionsAPI } from '../../requests/request';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import { useAppSelector } from '@redux/hooks';

interface PortConnectionsProps {
	portId: string | null;
  }
interface PortConnections {
	id: string | null;
	name: string;
	subid: string;
	internetEnterprise: InternetEnterprise;
	hardware: Hardware;
}
interface InternetEnterprise {
	vlan: number;
	mbps: number;
	subscriptionId: string;
	routing: string;
	terms: string
}
interface Hardware {
	status: number;
}

function normalizeVlan(v: unknown): { vlanNumeric: number; vlanDisplay: string } {
	if (v == null) return { vlanNumeric: 0, vlanDisplay: "-" };
	if (typeof v === "number") return { vlanNumeric: v, vlanDisplay: String(v) };
  
	const s = String(v).trim();
	if (!s) return { vlanNumeric: 0, vlanDisplay: "-" };
  
	// Split on ';' or ',' and clean parts
	const parts = s.split(/[;,]/).map(p => p.trim()).filter(Boolean);
	const display = parts.join(", ");
	const first = Number(parts[0]);
	const vlanNumeric = Number.isFinite(first) ? first : 0;
  
	return { vlanNumeric, vlanDisplay: display || "-" };
  }
  

  export default function PortConnections({ portId }: PortConnectionsProps) {
	
	if (!portId) {
		return <></>;
	}

	function capitalizeWords(str: string): string {
	return str
		.replace(/([A-Z])/g, " $1")
		.trim()
		.replace(/\b\w/g, (char) => char.toUpperCase());
	}

	function shapeConnection(connection: any): PortItem {
		const parent = connection.parent ?? {};
		const t = String(connection.type ?? "").toLowerCase();
		const isFwint = t === "fwint";
		const { vlanNumeric, vlanDisplay } = normalizeVlan(connection.vlan);
	// Routing (IE only, but harmless to compute)
	const routingRaw = connection.routing ?? "";
	const routingFormatted = routingRaw ? capitalizeWords(routingRaw) : "-";
	
	// TERMS: support either `terms` OR `{ term, term_unit }`
	const termCount = connection.terms ?? connection.term;
	const rawUnit = connection.term_unit;
	// normalize unit label (month(s) / year(s)
	const unit = rawUnit ? String(rawUnit).toLowerCase() : "months";
	const normalizedUnit =
		unit.startsWith("month") ? (Number(termCount) === 1 ? "month" : "months")
		: unit.startsWith("year") ? (Number(termCount) === 1 ? "year" : "years")
		: unit; // fallback unchanged
	const termsFormatted = termCount ? `${termCount} ${normalizedUnit}` : "-";
	
	// Mbps: IE uses `mbps`, FWint uses `capacity`
	const mbps =
		isFwint
		? Number(connection.capacity ?? 0)
		: Number(connection.mbps ?? 0);
	
	// VLAN may be string; coerce to number
	const vlan = connection.vlan != null ? Number(connection.vlan) : 0;
	
	const item: PortItem = {
		id: connection.id ?? "-",
		portName: parent.name ?? "",
		subid: parent.subscriptionId ?? "",
		internetEnterpriseVlan: vlan,
		internetEnterpriseMbps: mbps,
		internetEnterpriseTerms: termsFormatted,
		internetEnterpriseSubsId: connection.subscriptionId ?? "",
		internetEnterpriseRouting: routingFormatted,
		cloud: connection.cloud ?? '-',
		provider:connection.provider ?? '-',
		account:connection.account ?? '-',
		autoRenew: connection.autoRenew ? "Active" : "Inactive",
		automaticVlan: connection.automaticVlan ? "Yes" : "No",
		type: connection.type ?? "-",
		hardwareStatus: parent.hardware?.status ?? 0,
		name: connection.name ?? parent.name ?? "",
		vlan: vlanNumeric,
		vlanDisplay,
	};
	
	// If this is FWint and it embeds a FWaaS, keep its subscriptionId for the label
	if (isFwint && connection.fwaas) {
		item.fwintFwaasSubsId =
		connection.fwaas.subscriptionId ?? connection.fwaas.name ?? undefined;
	}
	
	return item;
	}
	
	
	const navigate = useNavigate();
	const { tenantId } = useAppSelector(state => state.selection);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchColumn, setSearchColumn] = useState("All");
	const [tableData, setTableData] = useState<PortItem[]>([]);
	const [isFetching, setIsFetching] = useState(false);
	const [hasFetched, setHasFetched] = useState<boolean>(false);
	const filteredData = getFilteredData(tableData, searchTerm, searchColumn);
	//Port id selected from MainPort
	const api = useAxiosAuth0();

	useEffect(() => {
		const fetchData = async () => {
		try {
		setIsFetching(true);
		const response = await fetchPortConnectionsAPI(api, portId, tenantId);
		const data = response.data.data;

		const arrify = <T,>(v: T | T[] | null | undefined): T[] =>
			Array.isArray(v) ? v : v ? [v] : [];

		const flattenedConnections: any[] = [];

		// Internet Enterprise
		arrify(data.internetEnterprise).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "Enterprise Internet",
			parent: data,
		});
		});

		// Datacenter Interconnect (API may return null or object)
		arrify(data.datacenterInterconnect).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "Datacenter Interconnect",
			parent: data,
		});
		});

		// Cloud Connectivity
		arrify(data.cloudConnectivity).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "Cloud Connectivity",
			parent: data,
		});
		});

		// FWINT
		arrify(data.fwint).slice(0, 1).forEach((conn: any) => {
			flattenedConnections.push({ ...conn, type: "FWint", parent: data });
		});

		// SD-WAN Interconnect (hub ↔ port link)
		arrify(data.sdwanInt).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "SD-WAN Interconnect",
			parent: data,
		});
		});
	
		const shapedConnections = flattenedConnections.map((conn: any) =>
			shapeConnection(conn)
		);
	
		setTableData(shapedConnections);
		} catch (error) {
		console.error("Error fetching port connections", error);
		} finally {
		setIsFetching(false);
		setHasFetched(true);
		}
	};
	
	fetchData();
	}, [api, portId]);
	

	const handleCreateConnection = () => {
		navigate("/kontrol/add-ipx")
	};
	const columns = portsDetailsColumns();
	
	const updatedColumns = columns.map((col: GenericTableColumn<PortItem>) => {
	if (col.header === "Connection Id") {
		return {
		...col,
		renderCell: (row: PortItem) => {
			let route = "";
			switch (row.type) {
			case "Enterprise Internet":
			route = buildDetailPath("internet", { connectionId: row.id });
			break;
			case "Datacenter Interconnect":
			route = buildDetailPath("datacenter", { datacenterId: row.id });
			break;
			case "Cloud Connectivity":
			route = buildDetailPath("cloudConnectivity", { connectionId: row.id });
			break;
			case "FWint":
			route = `/kontrol/fwaas`;
			break;
			default:
			route = "#";
		}
        if (row.type === "FWint") {
			const fwintId = row.internetEnterpriseSubsId || row.id || "-";
			const fwaasId = row.fwintFwaasSubsId;
			const label = fwaasId ? `${fwintId} [FWaaS ${fwaasId}]` : fwintId;
  
			return (
			<button
			{...viewHref(route)}
			onClick={() => navigate(route)}
			className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
			>
			{label}
			</button>
		);
		}

		// Default (non-FWint) behavior unchanged
		const fallback = row.internetEnterpriseSubsId || row.subid || row.id;
		return (
		<button
			{...viewHref(route.startsWith("/") ? route : null)}
			onClick={() => navigate(route)}
			className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
		>
			{fallback}
		</button>
		);
	},
	};
	}
	return col;
  });

  return (
    <>
		<div className="flex w-full justify-end mb-5">
			{/* <GenericButton
				label={
					<div className="flex items-center gap-2">
						<IconPlus className="w-4 h-4" />
						<span>Create Connection</span>
					</div>
				}
				variant="secondary"
				shape="ghost"
				size="md"
				disabled={true}
				onClick={handleCreateConnection}
			/> */}
		</div>
		<GenericCard 
			isLoading={isFetching}
			headerContent="Associated Connections"
			isCollapsible={false}
			bodyContent={
				<GenericTable
				columns={updatedColumns}
				data={filteredData}
				initialRowsPerPage={5}
				rowsPerPageOptions={[5, 10, 20]}
				width="100%"
				tableClassName="bg-semantic-bg-primary"
			/>
			}
		>
		</GenericCard>
	</>
  )
}
